import { useId } from 'react'

export interface OpcaoDeFiltro {
  valor: string
  rotulo: string
}

interface Props {
  legenda: string
  /**
   * Uma linha explicando o grupo, para quem não é de TI. Só onde o comportamento não se deduz das
   * caixas — por exemplo, o que significa não marcar nenhuma.
   */
  ajuda?: string
  opcoes: OpcaoDeFiltro[]
  selecionados: string[]
  aoAlternar: (valor: string, marcado: boolean) => void
}

/**
 * Grupo de caixas de seleção para um filtro de vários valores.
 *
 * Caixas, e não uma lista suspensa múltipla: `<select multiple>` exige Ctrl+clique para
 * marcar mais de um item, o que é invisível para quem não sabe, e some no celular. Aqui
 * tudo o que está disponível fica à vista, e o `<fieldset>` faz o leitor de tela anunciar
 * "Estado" antes de cada opção.
 *
 * @param props legenda, opções, o que está marcado e o que fazer ao alternar
 * @returns o grupo de caixas
 */
export function GrupoDeCaixas({ legenda, ajuda, opcoes, selecionados, aoAlternar }: Props) {
  const base = useId()
  const idDaAjuda = `${base}-ajuda`

  return (
    // `aria-describedby` e não um texto solto: assim o leitor de tela anuncia a explicação junto
    // do grupo, e não só para quem enxerga a linha abaixo da legenda.
    <fieldset className="min-w-0" aria-describedby={ajuda ? idDaAjuda : undefined}>
      <legend className="text-sm font-medium text-slate-800">{legenda}</legend>
      {ajuda && (
        <p id={idDaAjuda} className="mt-1 max-w-prose text-xs text-slate-600">
          {ajuda}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
        {opcoes.map((opcao) => {
          const id = `${base}-${opcao.valor}`
          return (
            <div key={opcao.valor} className="flex items-center gap-2">
              <input
                id={id}
                type="checkbox"
                value={opcao.valor}
                checked={selecionados.includes(opcao.valor)}
                onChange={(evento) => aoAlternar(opcao.valor, evento.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-niteroi-700 focus:ring-niteroi-600"
              />
              <label htmlFor={id} className="text-sm text-slate-700">
                {opcao.rotulo}
              </label>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}
